/**
 * One layout, many languages.
 *
 * Copy is resolved at render time from these dictionaries; no screen is authored
 * twice. Spanish and English ship together from the first screen, because
 * retrofitting i18n is a refactor and doing it now is a habit — see spec.md,
 * "Multilingual from the first commit".
 *
 * The dictionaries are typed against the English one, so a missing Spanish key
 * is a compile error rather than an English word appearing mid-sentence in a
 * Spanish paragraph.
 */
import { createContext, useContext } from 'react';
import type { Locale, Localised } from '@nabu/shared';

export const LOCALES: readonly Locale[] = ['es', 'en'];

/** Spanish is the default; English ships alongside it. */
export const DEFAULT_LOCALE: Locale = 'es';

const en = {
  'lang.name': 'English',
  'lang.switch': 'Change language',

  'nav.home': 'Home',

  'home.tagline': 'Learn it by being asked.',
  'home.lede':
    'Tell nabu what you want to memorise, add the books and pages you already study from, and say when you need to know it. It builds a plan and then teaches you over WhatsApp — asking more about what you keep getting wrong, and less about what you have.',

  'home.how.title': 'How it works',
  'home.how.1.title': 'Say what you want to learn',
  'home.how.1.body':
    'In your own words. Add PDFs, photographs of book pages, or links to anything you already study from.',
  'home.how.2.title': 'Review the plan',
  'home.how.2.body':
    'nabu researches the subject, reads your material and proposes a curriculum. You reorder it, weight it, and ask for more where it looks thin.',
  'home.how.3.title': 'Answer on WhatsApp',
  'home.how.3.body':
    'Questions arrive when you are reachable and at the moment you are about to forget. Wrong answers come back sooner; right ones come back later.',

  'home.why.title': 'Why it asks when it does',
  'home.why.body':
    'Every answer updates a model of what you remember, and the schedule follows from it — not from a fixed calendar. Say you have an exam on a date and the intervals compress toward it. Say you want to hold 85% and it paces itself to that instead.',

  'home.cta.title': 'Being built',
  'home.cta.body':
    'Signing in works. Stating an objective and getting a curriculum are next — so there is nothing to do inside yet, but you can get an account.',
  // Not "with WhatsApp" any more: F-101 makes a code by email a peer of the chat network,
  // and a front page that names one of the two ways in is wrong about the other.
  'home.cta.button': 'Sign in',

  'signin.title': 'Sign in',
  'signin.number.label': 'Your WhatsApp number',
  'signin.number.hint': 'With the country code. The code arrives on WhatsApp.',
  'signin.number.submit': 'Send me a code',
  'signin.code.label': 'The six-digit code',
  'signin.code.hint': 'It expires in ten minutes.',
  'signin.code.submit': 'Sign in',
  'signin.code.back': 'Change number',
  'signin.working': 'One moment…',
  'signin.done.title': 'You are in',
  'signin.done.body':
    'Nothing to do here yet — stating an objective is the next thing being built. Reload the page and you should still be signed in.',
  'signin.error.number': 'That does not look like a phone number with its country code.',
  'signin.error.code': 'That code is wrong or has expired. Ask for another.',
  'signin.error.tooMany': 'Too many codes requested. Wait a little and try again.',
  'signin.error.generic': 'Something went wrong. Try again.',

  // Signing in by email (F-101), beside WhatsApp rather than instead of it.
  'signin.channel.legend': 'Where should the code go?',
  'signin.channel.phone': 'WhatsApp',
  'signin.channel.email': 'Email',
  'signin.email.label': 'Your email address',
  'signin.email.hint': 'The code arrives by email, and nothing else is ever sent there.',
  'signin.email.back': 'Change address',
  'signin.error.email': 'That does not look like an email address.',
  'signin.error.unconfigured': 'Email sign-in is not switched on yet. Use WhatsApp for now.',

  'email.title': 'Signing in without WhatsApp',
  'email.body':
    'Add an email address and it becomes a second way into this same account — the same objectives, the same curriculum, the same history. Nothing is sent to it except a sign-in code.',
  'email.label': 'Email address',
  'email.submit': 'Send me a code',
  'email.code.label': 'The six-digit code',
  'email.code.hint': 'It expires in ten minutes.',
  'email.confirm': 'Add this address',
  'email.cancel': 'Cancel',
  'email.done': 'Done. You can now sign in with {email} as well as by WhatsApp.',
  'email.error.email': 'That does not look like an email address.',
  'email.error.code': 'That code is wrong or has expired. Ask for another.',
  'email.error.inUse': 'That address already signs in to another account.',
  'email.error.tooMany': 'Too many codes requested. Wait a little and try again.',
  'email.error.generic': 'Something went wrong. Try again.',

  'session.signedIn': 'Signed in',
  'session.signOut': 'Sign out',
  'session.objectives': 'My objectives',
  'session.chat': 'Conversation',

  'obj.new.title': 'What do you want to learn?',
  'obj.description.label': 'Describe it in your own words',
  'obj.description.placeholder':
    'For example: memorise the more important points across all meridians in TCM, including the five element points, source, connect, cleft, mu, shu, entry and exit — the number, name, location and description of each.',
  'obj.description.hint':
    'Write as much as you like. This exact text is what gets researched, so detail helps.',
  'obj.new.submit': 'Save it',
  'obj.working': 'Working…',
  'obj.target.label': 'How will you know you are done?',
  'obj.target.score': 'A score I want to hold',
  'obj.target.date': 'A date I need it by',
  'obj.target.scoreHint': 'how often you want to get them right',
  'obj.target.dateHint': 'intervals compress as the date approaches',
  'obj.list.title': 'Your objectives',
  'obj.list.loading': 'Loading…',
  'obj.list.empty': 'None yet.',
  'obj.status.drafting': 'Draft',
  'obj.status.researching': 'Researching',
  'obj.status.synthesising': 'Building the plan',
  'obj.status.review': 'Ready to review',
  'obj.status.active': 'Running',
  'obj.status.done': 'Done',
  'obj.status.abandoned': 'Abandoned',
  'obj.status.shelved': 'Set aside',
  'obj.current.description': 'What you asked for',
  'obj.target.save': 'Save target',
  'obj.target.saved': 'Saved.',
  'obj.close.done': 'I know this — mark it done',
  'obj.close.abandon': 'I am giving up on this',
  'obj.close.confirm': 'This closes the objective and lets you start another. Sure?',
  'obj.close.hint':
    'You work on one objective at a time. Closing this one opens the next — and nothing you close is lost, it goes to the list below and you can come back to it.',
  'obj.next.title': 'Ready for the next one',
  'obj.next.body': 'Your last objective is closed. Describe the next thing you want to learn.',

  'obj.history.title': 'Objectives you have closed',
  'obj.history.hint':
    'Nothing is running right now. Describe something new above, or pick one of these up again where you left it.',
  'obj.history.switchHint':
    'You have one running. Bringing another one back sets the current one aside — it keeps everything it has, lands in this list, and comes back the same way.',
  'obj.history.closed': 'closed',
  'obj.history.reactivate': 'Pick this up again',
  'obj.history.confirm':
    'This sets aside the objective you are on now and puts this one in its place. You are not giving up on it: it lands in this same list with everything it has, and you can come back to it whenever you want. Go ahead?',
  'obj.history.untitled': 'Objective without a title',
  'obj.history.switched': 'Done. The one you were on is set aside in this list.',
  'obj.history.resumed': 'Back on it.',

  'src.title': 'Your own material',
  'src.hint':
    'Optional. Photograph a page of your book, or add a PDF, and it will be read and used alongside the research.',
  /* The hint somebody sees when they arrived here from the gap notice. "Optional"
     is the wrong first word for the one thing on this screen only they can do. */
  'src.hint.wanted':
    'Your plan is missing a few things you asked for. A photograph of a page that covers any of them, or a PDF, is exactly what closes the gap.',
  'src.choose': 'Add pages or a PDF',
  'src.working': 'Reading it…',
  /* The promise the rest of F-92 keeps. It is said where the learner is standing when
     they finish adding a page, because that is the moment they used to go looking for
     the next button — and there were three of them. */
  'src.automatic':
    'Anything you add here goes into your plan on its own, and the questions for it are written straight after. You do not need to press anything else.',
  'src.empty': 'Nothing added.',
  'src.show': 'Show what was read',
  'src.hide': 'Hide',
  'src.loading': 'Loading…',
  'src.characters': 'characters',
  'src.truncated': 'truncated for display',
  'src.status.pending': 'Reading',
  'src.status.extracted': 'Read',
  'src.status.failed': 'Could not read',
  'src.error.type': 'That file type is not supported. Photographs and PDFs only.',
  'src.error.camera':
    'That is an iPhone photograph in HEIC, which cannot be read yet. In Settings → Camera → Formats, choose Most Compatible, or share the photo as a JPEG first.',
  'src.error.tooLarge': 'That file is too big. Photographs go up to',
  'src.error.upload': 'The upload did not complete. Try again.',
  'src.error.generic': 'Something went wrong with that file.',
  'src.failed.no_text': 'No text was found in this one. Try a straighter, brighter photograph.',
  'src.failed.too_large': 'This file is too big to read. Retake it at a smaller size.',
  'src.failed.unsupported_format': 'This file is not in a format that can be read.',
  'src.failed.too_many_pages': 'This PDF has too many pages. Add the chapter you are studying.',
  'src.failed.truncated':
    'One page of this holds more text than can be read in a single pass. A photograph of just that page usually works.',
  'src.retry': 'Try reading it again',
  'src.retrying': 'Trying again…',
  'src.remove': 'Remove',
  'src.removing': 'Removing…',
  'src.remove.confirm':
    'Remove this from your material? Anything read out of it goes too. Your plan keeps whatever it already learned from it until you rebuild.',
  'src.action.failed': 'That did not work. Try again.',
  'src.failed.internal': 'Something went wrong reading this one. It was not your file.',
  'obj.error.target': 'That target does not work — a date has to be in the future.',
  'obj.error.description': 'Say something about what you want to learn.',
  'obj.error.signIn': 'Sign in first.',
  'obj.error.generic': 'Something went wrong. Try again.',
  'obj.error.alreadyActive': 'You already have an objective running. Close it first.',
  'obj.error.notFound': 'That objective is not there any more. The list has been reloaded.',
  'obj.error.notClosed':
    'That one is not closed after all. The list has been reloaded — have another look.',
  'obj.error.conflict':
    'Something moved while you were looking at that list. It has been reloaded — have another look.',

  'cur.rebuild': 'Build this plan again',
  'cur.rebuild.body':
    'Not right? Building it again replaces this plan from the same research — it takes a couple of minutes and does not repeat the expensive research step.',
  'session.checking': 'One moment…',
  'win.pause.label': 'Send me questions',
  'win.pause.running': 'Questions are being sent inside the hours below.',
  'win.pause.stopped':
    'Stopped. Nothing is being sent, and nothing is lost — your plan and your progress are exactly where you left them. Turn this back on whenever you want.',
  'win.pause.stoppedBySilence':
    'Stopped automatically after a few unanswered reminders. Nothing is lost — turn this back on, or reply RESUME on WhatsApp.',
  'win.pause.failed': 'That did not save. Try again.',
  'start.title': 'Start learning',
  'start.body':
    'Your questions are ready. Starting means they begin arriving on WhatsApp inside the hours you set, and nothing arrives outside them.',
  'start.button': 'Start sending me questions',
  'start.confirm':
    'This starts sending questions to your WhatsApp inside the hours you set. You can pause it whenever you want. Ready?',
  'start.working': 'Starting…',
  'start.running': 'Running — questions are on their way.',
  'start.error.nothingActive':
    'Every subject is unticked, so there is nothing to send. Tick at least one above.',
  'start.error.noItems': 'There are no questions yet. Write them first, above.',
  /* Written as an invitation, never as an apology. The plan is not broken; it is
     incomplete in a way this learner, and only this learner, can close. */
  'gap.title': 'Things only you can add',
  'gap.body':
    'You asked for these and the research did not turn them up, so they are not in your plan and you will not be asked about them. A photograph of a page from your own book is what puts them in.',
  'gap.action': 'Upload a page that covers these',
  'gap.more': 'Show the rest',
  'gap.less': 'Show fewer',
  'gap.after.review':
    'Add the page first, then build the plan again further down: it re-reads everything, including what you just added, and this list gets shorter. It costs a couple of minutes and does not repeat the research.',
  /* Rewritten when folding became possible on a running objective. The old line
     said the plan stayed as it was until the next build, which was true then and
     is a lie now — and a notice that asks for a photograph and then says nothing
     will come of it is worse than no notice. */
  'gap.after.active':
    'Add the page first, then fold it in just below: it re-reads everything you have uploaded and adds what is new. Nothing you have already learned is lost, and your questions keep arriving the whole time.',

  /* The step after the upload, on an objective that is already sending.
     Every sentence here is load-bearing and each one answers a fear: that
     rebuilding costs the progress already made, that questions stop while it
     runs, and that this is instant. It is none of those, and saying so plainly
     is what makes the button safe to press. */
  'fold.title': 'Add what you have uploaded to your plan',
  'fold.body':
    'This re-reads everything you have added and folds anything new into the plan you are already working through. The topics you have been learning keep their place, and everything you have remembered about them stays — the plan only gains. Your questions carry on arriving throughout.',
  'fold.button': 'Add my uploads to the plan',
  'fold.working': 'Folding it in…',
  'fold.running':
    'Reading everything again and folding in what is new. A minute or two, and the questions for anything new are written after that. You can close this page — it carries on without you, and your questions keep coming.',
  'fold.error.running':
    'That is already happening — it started a moment ago and takes a minute or two.',
  'fold.error.notReady':
    'This objective has moved on since this page last looked at it. The page has been brought up to date.',
  'fold.failed':
    'The last attempt to add your material did not finish. Your plan and everything you have learned are exactly as they were — nothing was changed. What went wrong:',
  /* What the learner is told while the second half runs, and it exists because the
     second half used to be a button. Writing the questions is not a tidy-up after a
     fold — a topic with no questions is never sent — so this is the sentence that
     stops a plan that visibly gained a topic from being the end of the story. */
  'fold.writing':
    'Your plan has the new material. Writing the questions for it now — a few minutes, and only for what changed. You can close this page; it carries on without you.',
  /* The whole chain in one line, which is the entire point of doing it without asking.
     Leo, having pressed four buttons to get here: "the user should simply be told:
     these are the new topics and there are n new questions". */
  'fold.outcome':
    '{topics} new topics, {questions} new questions. They will start arriving with your usual ones.',
  /* Nothing was added but something was reworded, which is a real outcome and would
     otherwise be reported as "0 new topics". */
  'fold.reworded':
    '{topics} topics rewritten, {questions} new questions. They will start arriving with your usual ones.',
  /* Not a failure, and it must not read as one: re-reading material that is already in
     the plan is supposed to change nothing, and it costs nothing further. */
  'fold.nothingNew':
    'Everything you have added is already in your plan — there was nothing new to fold in, and no new questions were needed.',

  /* Asking for more research, which is the one control in this product that
     spends real money on being pressed. Every line here is written to be read
     *before* the press rather than explained after it: what it costs, what it
     does not change, and — the sentence it would be easy to leave out — that it
     may come back with nothing new. See F-51 and docs/costs.md. */
  'again.title': 'Ask for more research',
  'again.body':
    'nabu goes back to the web and reads up on the subject again. Whatever it finds is added to the material you already have: your plan, your questions and everything you have remembered stay exactly as they are until you fold the new material in yourself, further down.',
  'again.honest':
    'It runs the same search again from your description, so it may turn up sources it missed the first time and it may not. It takes five to fifteen minutes and costs about {cost} — by far the most expensive thing nabu does.',
  'again.button': 'Look again — {cost}',
  'again.working': 'Starting…',
  'again.running':
    'Looking again. Five to fifteen minutes, and what it finds appears above under “What the research found”. You can close the page — it carries on without you, and nothing about your plan or your questions changes while it runs.',
  /* Said out loud rather than left as a dead button. The lock is released by age
     rather than by the pass finishing, so the button really is down for half an
     hour, and an unexplained disabled control is how somebody concludes the
     feature is broken and reloads until it works. */
  'again.locked':
    'This button comes back half an hour after a pass starts, whether or not the pass has finished. That is deliberate on the one thing here that costs real money: nothing can spend it twice by accident.',
  'again.confirm':
    'This spends about {cost} on a fresh research pass. It adds to the material you already have and, on its own, changes nothing about your plan or your progress. Go ahead?',
  'again.error.running':
    'A research pass is already running on this objective. It takes five to fifteen minutes.',
  'again.error.notReady':
    'This objective is not somewhere a second research pass can start from. The page has been brought up to date.',
  'again.next': 'When it lands, fold it into your plan below. Nothing changes until you do.',

  /* Leaving. The whole of this panel is written on the assumption that somebody
     reading it is about to lose 476 review states and will not get them back, so
     nothing here is softened and nothing is implied. The one thing that is kept
     is named, because "we delete everything" with an unmentioned exception is the
     kind of sentence a privacy right is exercised against. */
  'del.title': 'Delete everything',
  'del.body':
    'This erases your account and everything in it: every objective, the plans built for them, every question written for you, everything you have remembered, your whole message history, the pages you photographed and the text read out of them, and the link between your WhatsApp number and this account. The messages stop the moment it runs.',
  'del.irreversible':
    'There is no undo and there is no copy. Nothing is archived, nothing can be recovered by asking, and signing in again with the same number starts you from nothing.',
  'del.kept':
    'One thing is kept: the monthly count of what nabu spent on model calls for this account. It holds no text — nothing you wrote, nothing you uploaded, only numbers, under an id that no longer belongs to anybody — and it is what the bill is checked against.',
  'del.ack': 'I understand this cannot be undone.',
  'del.button': 'Delete my account and everything in it',
  'del.working': 'Deleting…',
  'del.confirm':
    'Last check. This erases your objectives, your plans, your questions, everything you have remembered, your messages and the pages you uploaded. It cannot be undone. Delete it all?',
  'del.done.title': 'It is gone',
  'del.done.body': 'Your account and everything in it has been deleted, and you are signed out.',
  'del.done.counts':
    '{objectives} objectives, {nodes} topics, {items} questions, {reviews} things you had remembered, {messages} messages, and {objectVersions} stored files — counting every earlier version of them.',
  'del.done.home': 'Back to the start',
  /* A failure here is the one place a reassuring message would be a lie: a
     deletion that stopped halfway really has destroyed some of it. Saying so is
     the only honest option, and "try again, it carries on" is the only useful
     instruction. */
  'del.failed':
    'The deletion did not finish. Some of your data is already gone and some may still be there — this did not stop cleanly. Try again: it carries on from where it stopped. If it keeps failing, say so rather than assuming it worked.',
  'del.error.notFound': 'There is no account here to delete. You may already have deleted it.',
  'del.error.notConfirmed': 'That did not arrive confirmed. Tick the box and try again.',

  'res.title': 'What the research found',
  'res.hint':
    'This is the exact text your curriculum is built from. The end of it lists what the research could not find — worth a look before you build.',
  'res.characters': 'characters',
  'res.truncated': 'cut off here; the whole thing was used to build the curriculum',

  'items.title': 'Your questions',
  'items.build.body':
    'The plan above is what you will be asked about. Writing the questions takes a few minutes and happens in the background — you can close this and come back.',
  'items.build': 'Write my questions',
  'items.working': 'Writing them…',
  'items.started':
    'On it. They are being written in the background — this can take a few minutes, and nothing is lost if you close the page.',
  'items.ready': 'Ready',
  'items.rebuild': 'Write them again',
  'items.failed.body': 'The questions did not get written.',
  'items.error.notReady':
    'There is no curriculum to write questions about yet. Build the plan above first.',
  'items.error.alreadyRunning':
    'They are already being written for this plan. Give it a few minutes.',
  'items.folding':
    'Your plan is being updated right now. The questions can be written as soon as that finishes.',

  'obj.error.notDrafting':
    'The research has already been started for this one. The page has been brought up to date.',
  'obj.error.notReady': 'This objective is not ready for a curriculum yet.',
  'obj.error.noMaterial':
    'There is nothing to build a curriculum from yet — no research has landed, and nothing you added has been read.',

  'obj.research.title': 'Research this',
  'obj.research.body':
    'nabu reads up on the subject across the web and writes down what it finds, with the source of every claim. It takes five to fifteen minutes, and it runs once — this is the most expensive thing nabu does, so start it when the description above says what you mean.',
  'obj.research.start': 'Start the research',
  'obj.research.starting': 'Starting…',
  'obj.research.running.title': 'Reading up on it',
  'obj.research.running.body':
    'Five to fifteen minutes, usually. Close the tab if you like — it carries on without you, and this page moves on by itself when the findings land.',
  'obj.wait.slow':
    'Still going, and this page has stopped checking rather than sit here refreshing all afternoon. Nothing is lost.',
  'obj.wait.check': 'Check again',
  'obj.research.failed.title': 'The research stopped',
  'obj.research.failed.body':
    'It did not get far enough to leave anything behind, so the objective is a draft again. What went wrong:',
  'obj.research.retry': 'Start the research again',

  'cur.title': 'The plan',
  'cur.build.body':
    'The findings are in. Building the curriculum turns everything read so far into an ordered set of topics — a minute or two.',
  'cur.build': 'Build the curriculum',
  'cur.failed.body':
    'The last attempt to build the curriculum did not finish. Everything that was read is still there, so trying again pays for the build and not the research. What went wrong:',
  'cur.building.title': 'Building the curriculum',
  'cur.building.body':
    'One pass over everything read so far. Usually a minute or two, and the plan appears here when it is done.',
  'cur.empty': 'No plan yet.',
  'cur.topics': 'topics',
  'cur.weight.label': 'The band on each topic is how much of the plan it is worth — not how often you will be asked.',
  'cur.weight.core': 'Core',
  'cur.weight.major': 'Important',
  'cur.weight.minor': 'Supporting',
  'cur.weight.aside': 'On the edge',
  'cur.subjects': 'subjects',
  'cur.inside.one': 'topic inside',
  'cur.inside.many': 'topics inside',
  'cur.sections.openAll': 'Open every subject',
  'cur.sections.closeAll': 'Close every subject',
  'cur.active.hint':
    'Every subject starts ticked. Untick one and nabu writes no questions for it and never asks you about it — the rest of the plan carries on as it is.',
  'cur.active.aria': 'Study this subject',
  'cur.active.off': 'Not studying',
  'cur.active.failed':
    'That did not save, so it is back to how nabu still has it. Try again.',
  'cur.active.none':
    'Nothing is ticked, so there is nothing left for nabu to ask you about. Tick at least one subject.',
  'cur.summaries.show': 'Show the full descriptions',
  'cur.summaries.hide': 'Shorten the descriptions',
  'cur.untitled': 'Topic without a title',

  'prog.title': 'Where you are',
  'prog.loading': 'Loading…',
  'prog.unreadable':
    'Your progress could not be read. That is a fault here and not a comment on how you are doing — reload the page.',
  'prog.headline': 'of the plan, weighted by how much each subject is worth',
  'prog.headline.none':
    'There is nothing to measure yet: no questions have been written for the subjects you are studying.',
  'prog.counts':
    '{holding} held · {struggling} to work on · {untested} not asked yet, out of {questions} questions.',
  'prog.answered.none':
    'Nothing has been asked yet, so nothing is known about how you are doing. That is an empty result, not a bad one.',
  'prog.answered':
    'You have answered {answered} of {questions} questions. Across those {answered}, and only those, nabu puts your chance of getting one right today at {recall}.',
  'prog.pace.score':
    'There is no date to be on track for. You asked to hold {target}, which is the standard nabu schedules against.',
  'prog.pace.date.ok':
    '{days} days left. The plan needs about {required} questions a day and you accept up to {capacity}, so nothing about it is impossible yet — which is not the same as a promise.',
  'prog.pace.date.tight':
    '{days} days left. The plan needs about {required} questions a day and you accept {capacity}. It does not fit: move the date, untick some subjects, or take more questions a day.',
  'prog.load.title': 'Today',
  'prog.load':
    'Up to {limit} questions today. {sent} asked, {answered} answered, and {due} ready to ask.',
  'prog.load.noLimit':
    'You have not saved a schedule yet, so there is no daily cap of your own to report against.',
  'prog.load.ceiling':
    'nabu asks one question at a time and waits for your answer, so {limit} a day is a ceiling and not a forecast. Most days will be fewer. The short lessons that teach a topic, and any nudge about a question still waiting, do not count against it.',
  'prog.load.waiting': 'One question is outstanding. Nothing new goes out until you answer it.',
  'prog.load.paused': 'Sending is stopped, so nothing is going out at all.',
  'prog.load.pausedBySilence':
    'Sending stopped by itself after three reminders went unanswered. Turn it back on below and everything picks up where it was.',
  'prog.load.noZone':
    'We do not know your timezone, so nabu cannot tell which day it is where you are — the count above may be against the wrong day.',
  'prog.subjects.title': 'Subject by subject',
  'prog.subjects.byPlan': 'Show them in plan order',
  'prog.subjects.byWeakest': 'Show the weakest first',
  'prog.subject.counts':
    '{holding} of {questions} held · {struggling} to work on · {untested} not asked',
  'prog.subject.none': 'No questions yet',
  'prog.note.explanations':
    '{count} of the messages in this plan are explanations rather than questions. Nobody answers those, so they are left out of every number above.',
  'prog.note.noQuestions':
    '{count} subjects you are studying have no questions written for them. They are left out of the figure above rather than counted as nought.',
  'prog.note.unplaced':
    '{count} questions belong to topics that are no longer in the plan. They are still asked, and they are not counted under any subject.',
  'prog.note.off':
    'A subject you unticked after starting still has its questions in the rotation — unticking stops new ones being written, not ones already scheduled.',
  'prog.note.partial':
    'There are more questions here than this screen reads at once, so every number above is a floor rather than a total.',

  'sheet.title': 'What you have learned',
  'sheet.hint':
    'One line per fact you have actually been asked and answered — not the whole plan, and nothing you have not been taught yet. Made to be printed.',
  'sheet.download': 'Download it as a PDF',
  'sheet.working': 'Writing it…',
  'sheet.error.empty':
    'Nothing has been answered yet, so there is nothing to put on a sheet. It fills up as you answer questions on WhatsApp.',
  'sheet.error.rewrite':
    'The sheet could not be written this time. Nothing is lost — try again in a moment and it will pick up where it stopped.',
  'sheet.error.generic': 'The sheet could not be downloaded. Try again.',

  'win.title': 'When nabu may reach you',
  'win.hint':
    'This is a standing arrangement, not part of any one objective — it stays as you set it when you close one and start another.',
  'win.loading': 'Loading…',
  'win.defaults': 'Nothing saved yet, so these are the defaults.',
  'win.limit.label': 'How many questions a day, at most',
  'win.limit.unit': 'questions a day',
  'win.hours.label': 'The hours you are reachable',
  'win.hours.from': 'From',
  'win.hours.until': 'Until',
  'win.hours.hint':
    'Outside these hours nothing is sent — it waits. Both times are read on your own clock.',
  'win.explanations.label': 'Send explanations, not only questions',
  'win.explanations.hint':
    'Off, and nabu only ever asks you things. On, and it teaches you a topic in a few hundred words before asking about it, and teaches it again if you start missing. These do not count against your daily questions. Either way a wrong answer is still corrected.',
  'win.plain': '{count} questions a day between {from} and {until} — about one every {gap}.',
  'win.plain.one': 'One question a day, somewhere between {from} and {until}.',
  'win.plain.quiet': 'Nothing at all between {until} and {from}.',
  'win.plain.explanations.on':
    'Short lessons arrive on top of that, as many as the material needs — they are how a topic is taught before it is asked about, so they do not use up your questions.',
  'win.plain.explanations.off': 'Questions only. Nothing is sent to read first.',
  'win.unit.hours': 'h',
  'win.unit.minutes': 'min',
  'win.zone': 'Read on the clock where you are:',
  'win.zone.missing':
    'We do not know which timezone you are in, so these hours cannot be honoured yet — hours without a timezone are not a schedule. Sign in again from the phone you use and it gets picked up.',
  'win.save': 'Save this',
  'win.saved': 'Saved.',
  'win.working': 'Saving…',
  'win.error.load': 'Could not read your current schedule. Reload the page.',
  'win.error.limit': 'Between 1 and 24 questions a day. Zero is not a schedule — close the objective instead.',
  'win.error.limitWhole': 'A whole number of questions a day.',
  'win.error.time': 'Those hours could not be read. Use the two time pickers.',
  'win.error.empty': 'The start and the end are the same time, which leaves no hours to reach you in.',
  'win.error.wrap':
    'The hours have to start and finish on the same day. If you want to be reached late at night, end at 23:59 rather than after midnight.',
  'win.error.generic': 'That did not save. Try again.',

  /*
   * The conversation (F-100).
   *
   * `chat.hint` names three words the learner may type — and they are copy here rather than
   * imported, because `MORE_WORD`, `DISPUTE_WORD` and the `n MÁS PREGUNTAS` form live in
   * `packages/messaging` beside the recognisers that read them, and pulling that package
   * into a browser bundle would ship the AWS SDK to a phone for the sake of three strings.
   * Renaming one of those words means editing this line; grep for the constants.
   */
  'chat.title': 'The conversation',
  'chat.lede':
    'The same thread that reaches you on WhatsApp — the questions, what you answered, and what you were told. Answer here and it is marked exactly the same way.',
  'chat.loading': 'Reading the conversation…',
  'chat.empty':
    'Nothing yet. The first question arrives once an objective is running and you are inside the hours you chose.',
  'chat.older': 'Show earlier messages',
  'chat.input.label': 'Your answer',
  'chat.input.placeholder': 'Answer the question, or ask for something.',
  'chat.send': 'Send',
  'chat.sending': 'Sending…',
  'chat.retry': 'Send it again',
  'chat.said.you': 'You said:',
  'chat.said.nabu': 'nabu said:',
  'chat.hint':
    'You can also reply MORE for more detail on the last thing you were taught, DISPUTE if a mark is wrong, or “5 MORE QUESTIONS” to keep going once the day is spent.',
  'chat.polling': 'New messages appear within twenty seconds while this tab is open.',
  'chat.error.load': 'The conversation could not be read. Reload the page.',
  'chat.error.empty': 'There is nothing to send.',
  'chat.error.long':
    'That is longer than the conversation can keep, so it would be stored differently from how it was read. Shorten it and send it again.',
  'chat.error.session': 'This session is no longer valid. Sign in again.',
  'chat.error.graded':
    'That answer was marked, but the reply did not come back. Sending it again would not help — the question is closed and the next one still arrives.',
  'chat.error.send': 'That did not send. Try again.',
  'chat.error.offline': 'No connection. Try again once you have one.',

  /*   * Notifications (F-102).
   *
   * The copy is doing more work here than on any other panel, because the state a
   * learner is in is decided by three things they cannot see at once: whether this
   * browser can do notifications at all, whether they have granted permission, and
   * whether the server still has a row for this device. Every one of those has its own
   * sentence rather than a shared "something went wrong", since the action differs —
   * install the app, change a browser setting, or press the button again.
   */
  'push.title': 'Notifications on this device',
  'push.hint':
    'nabu can reach you here instead of on WhatsApp. Turn it on once per phone, tablet or computer you want to be asked on — this is a setting for this device, not for your account.',
  'push.checking': 'Checking…',
  'push.on': 'On for this device.',
  'push.off': 'Off for this device.',
  'push.enable': 'Turn on notifications',
  'push.disable': 'Turn them off here',
  'push.working': 'One moment…',
  'push.devices.none': 'No device is registered yet.',
  'push.devices.one': 'One device registered.',
  'push.devices.many': '{count} devices registered.',
  /*
   * The state where the browser and the server disagree, said plainly.
   *
   * This browser holds a subscription and the server has no row for it — which happens
   * after a subscription expires and gets pruned, or after an account is erased. It is
   * the one state in which everything looks on and nothing arrives, so it gets its own
   * sentence and its own instruction rather than being folded into "on".
   */
  'push.stale':
    'This device thinks notifications are on, but nabu has no record of it. Turn them off and on again here.',
  'push.install.title': 'Add nabu to your home screen first',
  'push.install.body':
    'On iPhone and iPad, notifications only work once the app is on your home screen. Tap Share, then “Add to Home Screen”, then open nabu from there and come back to this setting.',
  'push.unsupported':
    'This browser cannot show notifications. Chrome, Safari 16.4 or later, and Firefox can.',
  'push.denied':
    'This browser is blocking notifications from nabu. Nothing here can undo that — it has to be changed in the browser’s own settings for this site, and then you can turn them on again.',
  'push.error.notConfigured':
    'Notifications are not set up on this service yet. Nothing is wrong with your device.',
  'push.error.generic': 'That did not work. Try again.',
  'push.test': 'Send me a test',
  'push.test.hint':
    'It arrives the way nabu reaches you today — so if you are still on WhatsApp, it arrives there. That is the point: it tells you which one is live.',
  'push.test.sent': 'On its way. If nothing arrives in a minute, notifications are not reaching you.',
  'push.test.working': 'Sending…',
  'push.test.error': 'The test could not be sent. Try again in a moment.',

  'footer.built': 'Built in Costa Rica.',
} as const;

export type CopyKey = keyof typeof en;

const es: Record<CopyKey, string> = {
  'lang.name': 'Español',
  'lang.switch': 'Cambiar idioma',

  'nav.home': 'Inicio',

  'home.tagline': 'Apréndelo porque te lo preguntan.',
  'home.lede':
    'Dile a nabu qué quieres memorizar, agrega los libros y las páginas con las que ya estudias, y di para cuándo lo necesitas. Arma un plan y te enseña por WhatsApp: pregunta más sobre lo que se te sigue olvidando y menos sobre lo que ya sabes.',

  'home.how.title': 'Cómo funciona',
  'home.how.1.title': 'Di qué quieres aprender',
  'home.how.1.body':
    'Con tus propias palabras. Agrega PDF, fotos de páginas de tus libros o enlaces a lo que ya estudias.',
  'home.how.2.title': 'Revisa el plan',
  'home.how.2.body':
    'nabu investiga el tema, lee tu material y propone un temario. Vos lo reordenás, le das peso y pedís más donde se vea flojo.',
  'home.how.3.title': 'Respondé por WhatsApp',
  'home.how.3.body':
    'Las preguntas llegan cuando estás disponible y justo antes de que se te olvide. Lo que fallás vuelve pronto; lo que sabés vuelve más tarde.',

  'home.why.title': 'Por qué pregunta cuando pregunta',
  'home.why.body':
    'Cada respuesta actualiza un modelo de lo que recordás, y de ahí sale el horario — no de un calendario fijo. Si decís que tenés examen una fecha, los intervalos se comprimen hacia ella. Si decís que querés mantener un 85%, se acomoda a eso.',

  'home.cta.title': 'En construcción',
  'home.cta.body':
    'Entrar ya funciona. Definir un objetivo y recibir un temario son lo siguiente — así que todavía no hay nada que hacer adentro, pero ya podés tener cuenta.',
  'home.cta.button': 'Entrar',

  'signin.title': 'Entrar',
  'signin.number.label': 'Tu número de WhatsApp',
  'signin.number.hint': 'Con el código de país. El código llega por WhatsApp.',
  'signin.number.submit': 'Enviarme un código',
  'signin.code.label': 'El código de seis dígitos',
  'signin.code.hint': 'Vence en diez minutos.',
  'signin.code.submit': 'Entrar',
  'signin.code.back': 'Cambiar número',
  'signin.working': 'Un momento…',
  'signin.done.title': 'Ya estás dentro',
  'signin.done.body':
    'Todavía no hay nada que hacer aquí — definir un objetivo es lo siguiente que se está construyendo. Recargá la página y deberías seguir dentro.',
  'signin.error.number': 'Eso no parece un número de teléfono con código de país.',
  'signin.error.code': 'Ese código está mal o ya venció. Pedí otro.',
  'signin.error.tooMany': 'Se pidieron demasiados códigos. Esperá un rato y volvé a intentar.',
  'signin.error.generic': 'Algo salió mal. Intentá de nuevo.',

  'signin.channel.legend': '¿A dónde te mandamos el código?',
  'signin.channel.phone': 'WhatsApp',
  'signin.channel.email': 'Correo',
  'signin.email.label': 'Tu correo electrónico',
  'signin.email.hint': 'El código llega por correo, y ahí nunca se manda nada más.',
  'signin.email.back': 'Cambiar correo',
  'signin.error.email': 'Eso no parece un correo electrónico.',
  'signin.error.unconfigured': 'Entrar por correo todavía no está activo. Usá WhatsApp por ahora.',

  'email.title': 'Entrar sin WhatsApp',
  'email.body':
    'Agregá un correo y queda como una segunda forma de entrar a esta misma cuenta — los mismos objetivos, el mismo plan, la misma historia. Ahí no se manda nada más que el código para entrar.',
  'email.label': 'Correo electrónico',
  'email.submit': 'Enviarme un código',
  'email.code.label': 'El código de seis dígitos',
  'email.code.hint': 'Vence en diez minutos.',
  'email.confirm': 'Agregar este correo',
  'email.cancel': 'Cancelar',
  'email.done': 'Listo. Ya podés entrar con {email} además de por WhatsApp.',
  'email.error.email': 'Eso no parece un correo electrónico.',
  'email.error.code': 'Ese código está mal o ya venció. Pedí otro.',
  'email.error.inUse': 'Ese correo ya entra a otra cuenta.',
  'email.error.tooMany': 'Se pidieron demasiados códigos. Esperá un rato y volvé a intentar.',
  'email.error.generic': 'Algo salió mal. Intentá de nuevo.',

  'session.signedIn': 'Dentro',
  'session.signOut': 'Salir',
  'session.objectives': 'Mis objetivos',
  'session.chat': 'Conversación',

  'obj.new.title': '¿Qué querés aprender?',
  'obj.description.label': 'Describilo con tus propias palabras',
  'obj.description.placeholder':
    'Por ejemplo: memorizar los puntos más importantes de todos los meridianos en MTC, incluyendo los cinco puntos de los elementos, fuente, conexión, hendidura, mu, shu, entrada y salida — el número, nombre, ubicación y descripción de cada uno.',
  'obj.description.hint':
    'Escribí todo lo que querás. Este texto exacto es lo que se investiga, así que el detalle ayuda.',
  'obj.new.submit': 'Guardar',
  'obj.working': 'Trabajando…',
  'obj.target.label': '¿Cómo vas a saber que ya está?',
  'obj.target.score': 'Un porcentaje que quiero mantener',
  'obj.target.date': 'Una fecha para la que lo necesito',
  'obj.target.scoreHint': 'con qué frecuencia querés acertarlos',
  'obj.target.dateHint': 'los intervalos se comprimen al acercarse la fecha',
  'obj.list.title': 'Tus objetivos',
  'obj.list.loading': 'Cargando…',
  'obj.list.empty': 'Todavía ninguno.',
  'obj.status.drafting': 'Borrador',
  'obj.status.researching': 'Investigando',
  'obj.status.synthesising': 'Armando el plan',
  'obj.status.review': 'Listo para revisar',
  'obj.status.active': 'En marcha',
  'obj.status.done': 'Terminado',
  'obj.status.abandoned': 'Abandonado',
  'obj.status.shelved': 'De lado',
  'obj.current.description': 'Lo que pediste',
  'obj.target.save': 'Guardar meta',
  'obj.target.saved': 'Guardado.',
  'obj.close.done': 'Ya me lo sé — darlo por terminado',
  'obj.close.abandon': 'Ya no lo voy a seguir',
  'obj.close.confirm': 'Esto cierra el objetivo y te deja empezar otro. ¿Seguro?',
  'obj.close.hint':
    'Trabajás en un objetivo a la vez. Cerrar este abre el siguiente — y nada de lo que cerrás se pierde: queda en la lista de abajo y podés volver a él.',
  'obj.next.title': 'Listo para el siguiente',
  'obj.next.body': 'Tu objetivo anterior está cerrado. Describí lo próximo que querés aprender.',

  'obj.history.title': 'Objetivos que cerraste',
  'obj.history.hint':
    'Ahora no hay ninguno en marcha. Describí algo nuevo arriba, o retomá uno de estos donde lo dejaste.',
  'obj.history.switchHint':
    'Tenés uno en marcha. Si traés otro de vuelta, el de ahora queda de lado — conserva todo lo que tiene, cae en esta lista y se retoma igual.',
  'obj.history.closed': 'cerrado el',
  'obj.history.reactivate': 'Retomar este',
  'obj.history.confirm':
    'Esto deja de lado el objetivo en el que estás y pone este en su lugar. No lo estás abandonando: cae en esta misma lista con todo lo que tiene, y lo podés retomar cuando querás. ¿Le damos?',
  'obj.history.untitled': 'Objetivo sin título',
  'obj.history.switched': 'Listo. El que tenías en marcha quedó de lado en esta lista.',
  'obj.history.resumed': 'De vuelta en este.',

  'src.title': 'Tu propio material',
  'src.hint':
    'Opcional. Sacale una foto a una página de tu libro, o agregá un PDF, y se lee para usarlo junto con la investigación.',
  'src.hint.wanted':
    'A tu plan le faltan unas cosas que pediste. Una foto de una página que cubra alguna de ellas, o un PDF, es justo lo que cierra el hueco.',
  'src.choose': 'Agregar páginas o un PDF',
  'src.working': 'Leyéndolo…',
  'src.automatic':
    'Todo lo que agregues acá se suma al plan solo, y las preguntas de eso se escriben enseguida. No hace falta que aprietes nada más.',
  'src.empty': 'Nada agregado.',
  'src.show': 'Ver lo que se leyó',
  'src.hide': 'Ocultar',
  'src.loading': 'Cargando…',
  'src.characters': 'caracteres',
  'src.truncated': 'recortado para mostrarlo',
  'src.status.pending': 'Leyendo',
  'src.status.extracted': 'Leído',
  'src.status.failed': 'No se pudo leer',
  'src.error.type': 'Ese tipo de archivo no sirve. Solo fotos y PDF.',
  'src.error.camera':
    'Esa es una foto de iPhone en HEIC y todavía no se puede leer. En Ajustes → Cámara → Formatos, elegí Más compatible, o compartí la foto como JPEG antes de subirla.',
  'src.error.tooLarge': 'Ese archivo es muy grande. Las fotos llegan hasta',
  'src.error.upload': 'La subida no se completó. Probá de nuevo.',
  'src.error.generic': 'Algo salió mal con ese archivo.',
  'src.failed.no_text': 'No se encontró texto en esta. Probá una foto más derecha y con más luz.',
  'src.failed.too_large':
    'Este archivo es muy grande para leerlo. Volvé a tomarlo en un tamaño menor.',
  'src.failed.unsupported_format': 'Este archivo no está en un formato que se pueda leer.',
  'src.failed.too_many_pages':
    'Este PDF tiene demasiadas páginas. Dividilo y agregá la parte que estás estudiando.',
  'src.failed.truncated':
    'Una página de esto tiene más texto del que se puede leer de una sola vez. Sacale una foto solo a esa página y suele salir.',
  'src.retry': 'Intentar leerlo de nuevo',
  'src.retrying': 'Intentando de nuevo…',
  'src.remove': 'Quitar',
  'src.removing': 'Quitando…',
  'src.remove.confirm':
    '¿Quitar esto de tu material? Lo que se haya leído de ahí también se va. Tu plan se queda con lo que ya aprendió de eso hasta que lo reconstruyas.',
  'src.action.failed': 'Eso no funcionó. Probá de nuevo.',
  'src.failed.internal': 'Algo salió mal al leer esta. No fue tu archivo.',
  'obj.error.target': 'Ese objetivo no sirve — la fecha tiene que ser futura.',
  'obj.error.description': 'Contá algo de lo que querés aprender.',
  'obj.error.signIn': 'Entrá primero.',
  'obj.error.generic': 'Algo salió mal. Intentá de nuevo.',
  'obj.error.alreadyActive': 'Ya tenés un objetivo en marcha. Cerralo primero.',
  'obj.error.notFound': 'Ese objetivo ya no está. Se recargó la lista.',
  'obj.error.notClosed': 'Ese no está cerrado. Se recargó la lista — mirá de nuevo.',
  'obj.error.conflict': 'Algo se movió mientras mirabas esa lista. Se recargó — mirá de nuevo.',

  'cur.rebuild': 'Armar el plan de nuevo',
  'cur.rebuild.body':
    '¿No quedó bien? Armarlo de nuevo reemplaza este plan usando la misma investigación — toma un par de minutos y no repite el paso caro.',
  'session.checking': 'Un momento…',
  'win.pause.label': 'Mandame preguntas',
  'win.pause.running': 'Se te están mandando preguntas dentro de las horas de abajo.',
  'win.pause.stopped':
    'Detenido. No se está mandando nada, y no se pierde nada — tu plan y tu progreso quedan igual. Volvelo a activar cuando querás.',
  'win.pause.stoppedBySilence':
    'Se detuvo solo después de unos recordatorios sin respuesta. No se perdió nada — activalo de nuevo, o mandá SEGUIR por WhatsApp.',
  'win.pause.failed': 'Eso no se guardó. Probá de nuevo.',
  'start.title': 'Empezar a aprender',
  'start.body':
    'Tus preguntas están listas. Al empezar, te van llegando por WhatsApp dentro de las horas que pusiste, y nada llega fuera de ellas.',
  'start.button': 'Empezar a mandarme preguntas',
  'start.confirm':
    'Esto empieza a mandarte preguntas por WhatsApp dentro de las horas que pusiste. Lo podés pausar cuando querás. ¿Le damos?',
  'start.working': 'Empezando…',
  'start.running': 'En marcha — las preguntas van en camino.',
  'start.error.nothingActive':
    'Están todas las materias destildadas, así que no hay nada que mandar. Tildá al menos una arriba.',
  'start.error.noItems': 'Todavía no hay preguntas. Escribilas primero, arriba.',
  'gap.title': 'Lo que solo vos podés agregar',
  'gap.body':
    'Esto lo pediste y la investigación no lo encontró, así que no está en tu plan y no se te va a preguntar. Una foto de una página de tu propio libro es justo lo que lo agrega.',
  'gap.action': 'Subí una página que cubra esto',
  'gap.more': 'Ver el resto',
  'gap.less': 'Ver menos',
  'gap.after.review':
    'Primero agregá la página y después armá el plan de nuevo más abajo: vuelve a leer todo, incluido lo que acabás de agregar, y esta lista se acorta. Toma un par de minutos y no repite la investigación.',
  'gap.after.active':
    'Primero agregá la página y después sumala al plan acá abajo: vuelve a leer todo lo que subiste y agrega lo nuevo. No se pierde nada de lo que ya aprendiste, y las preguntas te siguen llegando todo el tiempo.',

  'fold.title': 'Sumá al plan lo que subiste',
  'fold.body':
    'Vuelve a leer todo lo que agregaste y suma al plan lo que sea nuevo. Los temas que venís estudiando quedan en su lugar, y todo lo que ya te aprendiste de ellos se mantiene — el plan solo crece. Las preguntas te siguen llegando mientras tanto.',
  'fold.button': 'Agregar al plan lo que subí',
  'fold.working': 'Sumándolo…',
  'fold.running':
    'Está leyendo todo de nuevo y sumando lo nuevo. Un minuto o dos, y las preguntas de eso se escriben después. Podés cerrar la página: sigue sin vos, y tus preguntas no se detienen.',
  'fold.error.running': 'Eso ya está pasando: arrancó hace un momento y toma un minuto o dos.',
  'fold.error.notReady':
    'Este objetivo cambió desde la última vez que esta página lo miró. La página ya se actualizó.',
  'fold.failed':
    'El último intento de sumar tu material no terminó. Tu plan y todo lo que aprendiste quedaron igual: no se cambió nada. Qué pasó:',
  'fold.writing':
    'Tu plan ya tiene el material nuevo. Ahora está escribiendo las preguntas de eso: unos minutos, y solo de lo que cambió. Podés cerrar la página; sigue sin vos.',
  'fold.outcome':
    '{topics} temas nuevos, {questions} preguntas nuevas. Te van a empezar a llegar junto con las de siempre.',
  'fold.reworded':
    '{topics} temas reescritos, {questions} preguntas nuevas. Te van a empezar a llegar junto con las de siempre.',
  'fold.nothingNew':
    'Todo lo que agregaste ya estaba en tu plan: no había nada nuevo para sumar, y no hizo falta ninguna pregunta nueva.',

  'again.title': 'Pedir más investigación',
  'again.body':
    'nabu vuelve a la web y lee otra vez sobre el tema. Lo que encuentre se suma al material que ya tenés: tu plan, tus preguntas y todo lo que llevás aprendido quedan exactamente igual hasta que vos mismo sumes lo nuevo al plan, más abajo.',
  'again.honest':
    'Corre la misma búsqueda de nuevo a partir de tu descripción, así que puede traer fuentes que se le escaparon la primera vez, o puede no traer nada nuevo. Toma de cinco a quince minutos y cuesta unos {cost} — de lejos lo más caro que hace nabu.',
  'again.button': 'Buscar otra vez — {cost}',
  'again.working': 'Arrancando…',
  'again.running':
    'Buscando otra vez. De cinco a quince minutos, y lo que encuentre aparece arriba en «Lo que encontró la investigación». Podés cerrar la página: sigue sin vos, y mientras tanto no cambia nada de tu plan ni de tus preguntas.',
  'again.locked':
    'Este botón vuelve media hora después de arrancar una búsqueda, haya terminado o no. Es a propósito, en lo único de acá que cuesta plata de verdad: nada lo puede gastar dos veces sin querer.',
  'again.confirm':
    'Esto gasta unos {cost} en una investigación nueva. Se suma al material que ya tenés y, por sí solo, no cambia nada de tu plan ni de tu progreso. ¿Le damos?',
  'again.error.running':
    'Ya hay una investigación corriendo en este objetivo. Toma de cinco a quince minutos.',
  'again.error.notReady':
    'Este objetivo no está en un punto donde se pueda arrancar otra investigación. La página ya se actualizó.',
  'again.next': 'Cuando llegue, sumalo al plan acá abajo. Hasta que lo hagás, no cambia nada.',

  'del.title': 'Borrar todo',
  'del.body':
    'Esto borra tu cuenta y todo lo que hay en ella: cada objetivo, los planes que se armaron, cada pregunta escrita para vos, todo lo que llevás aprendido, todo tu historial de mensajes, las páginas que fotografiaste y el texto que se leyó de ellas, y el vínculo entre tu número de WhatsApp y esta cuenta. Los mensajes se detienen en el momento en que corre.',
  'del.irreversible':
    'No hay deshacer y no hay copia. No se archiva nada, no se recupera nada pidiéndolo, y si volvés a entrar con el mismo número empezás desde cero.',
  'del.kept':
    'Se guarda una sola cosa: el conteo mensual de lo que nabu gastó en llamadas al modelo para esta cuenta. No tiene texto — nada de lo que escribiste, nada de lo que subiste, solo números, bajo un id que ya no es de nadie — y es contra lo que se revisa la factura.',
  'del.ack': 'Entiendo que esto no se puede deshacer.',
  'del.button': 'Borrar mi cuenta y todo lo que hay en ella',
  'del.working': 'Borrando…',
  'del.confirm':
    'Última revisión. Esto borra tus objetivos, tus planes, tus preguntas, todo lo que llevás aprendido, tus mensajes y las páginas que subiste. No se puede deshacer. ¿Borramos todo?',
  'del.done.title': 'Listo, ya no está',
  'del.done.body': 'Tu cuenta y todo lo que había en ella se borraron, y ya saliste de la sesión.',
  'del.done.counts':
    '{objectives} objetivos, {nodes} temas, {items} preguntas, {reviews} cosas que llevabas aprendidas, {messages} mensajes y {objectVersions} archivos guardados — contando cada versión anterior de ellos.',
  'del.done.home': 'Volver al inicio',
  'del.failed':
    'El borrado no terminó. Parte de tus datos ya no está y parte puede seguir ahí: esto no se detuvo limpio. Probá de nuevo, que sigue desde donde se quedó. Si vuelve a fallar, decilo en vez de dar por hecho que funcionó.',
  'del.error.notFound': 'Acá no hay cuenta que borrar. Puede que ya la hayas borrado.',
  'del.error.notConfirmed': 'Eso no llegó confirmado. Marcá la casilla y probá de nuevo.',

  'res.title': 'Lo que encontró la investigación',
  'res.hint':
    'Este es el texto exacto con el que se armó tu plan. Al final está lo que no se pudo encontrar — vale la pena verlo antes de armarlo.',
  'res.characters': 'caracteres',
  'res.truncated': 'cortado aquí; se usó todo para armar el plan',

  'items.title': 'Tus preguntas',
  'items.build.body':
    'El plan de arriba es sobre lo que te vamos a preguntar. Escribir las preguntas toma unos minutos y pasa en el fondo — podés cerrar esto y volver después.',
  'items.build': 'Escribir mis preguntas',
  'items.working': 'Escribiéndolas…',
  'items.started':
    'Listo, ya están escribiéndose en el fondo — puede tomar unos minutos, y no se pierde nada si cerrás la página.',
  'items.ready': 'Listas',
  'items.rebuild': 'Escribirlas de nuevo',
  'items.failed.body': 'Las preguntas no se escribieron.',
  'items.error.notReady':
    'Todavía no hay plan sobre el que escribir preguntas. Armá primero el de arriba.',
  'items.error.alreadyRunning':
    'Ya se están escribiendo para este plan. Dale unos minutos.',
  'items.folding':
    'Tu plan se está actualizando ahora mismo. Las preguntas se pueden escribir apenas termine.',

  'obj.error.notDrafting': 'La investigación de este ya estaba arrancada. Se actualizó la página.',
  'obj.error.notReady': 'Este objetivo todavía no está listo para un temario.',
  'obj.error.noMaterial':
    'Todavía no hay con qué armar el temario — no llegó ninguna investigación y no se leyó nada de lo que agregaste.',

  'obj.research.title': 'Investigar esto',
  'obj.research.body':
    'nabu investiga el tema en la web y anota lo que encuentra, con la fuente de cada dato. Toma entre cinco y quince minutos, y corre una sola vez — es lo más caro que hace nabu, así que arrancá cuando la descripción de arriba diga lo que querés decir.',
  'obj.research.start': 'Arrancar la investigación',
  'obj.research.starting': 'Arrancando…',
  'obj.research.running.title': 'Leyendo sobre el tema',
  'obj.research.running.body':
    'Entre cinco y quince minutos, normalmente. Cerrá la pestaña si querés — sigue sin vos, y esta página avanza sola cuando lleguen los hallazgos.',
  'obj.wait.slow':
    'Todavía sigue, y esta página dejó de revisar en vez de quedarse recargando toda la tarde. No se perdió nada.',
  'obj.wait.check': 'Revisar de nuevo',
  'obj.research.failed.title': 'La investigación se detuvo',
  'obj.research.failed.body':
    'No llegó a dejar nada, así que el objetivo volvió a ser un borrador. Qué pasó:',
  'obj.research.retry': 'Arrancar la investigación otra vez',

  'cur.title': 'El plan',
  'cur.build.body':
    'Ya están los hallazgos. Armar el temario convierte todo lo leído hasta ahora en un conjunto ordenado de temas — un minuto o dos.',
  'cur.build': 'Armar el temario',
  'cur.failed.body':
    'El último intento de armar el temario no terminó. Todo lo que se leyó sigue ahí, así que volver a intentarlo paga el armado y no la investigación. Qué pasó:',
  'cur.building.title': 'Armando el temario',
  'cur.building.body':
    'Una pasada sobre todo lo leído hasta ahora. Normalmente un minuto o dos, y el plan aparece aquí cuando esté.',
  'cur.empty': 'Todavía no hay plan.',
  'cur.topics': 'temas',
  'cur.weight.label': 'La banda de cada tema dice cuánto del plan vale — no con qué frecuencia te van a preguntar.',
  'cur.weight.core': 'Clave',
  'cur.weight.major': 'Importante',
  'cur.weight.minor': 'De apoyo',
  'cur.weight.aside': 'Al margen',
  'cur.subjects': 'materias',
  'cur.inside.one': 'tema dentro',
  'cur.inside.many': 'temas dentro',
  'cur.sections.openAll': 'Abrir todas las materias',
  'cur.sections.closeAll': 'Cerrar todas las materias',
  'cur.active.hint':
    'Todas las materias arrancan tildadas. Si destildás una, nabu no le escribe preguntas ni te pregunta nada de ella — el resto del plan sigue igual.',
  'cur.active.aria': 'Estudiar esta materia',
  'cur.active.off': 'Sin estudiar',
  'cur.active.failed': 'No se guardó, así que quedó como nabu lo sigue teniendo. Probá de nuevo.',
  'cur.active.none':
    'No hay nada tildado, así que a nabu no le queda nada que preguntarte. Tildá al menos una materia.',
  'cur.summaries.show': 'Ver las descripciones completas',
  'cur.summaries.hide': 'Acortar las descripciones',
  'cur.untitled': 'Tema sin título',

  'prog.title': 'En qué vas',
  'prog.loading': 'Cargando…',
  'prog.unreadable':
    'No se pudo leer tu progreso. Es una falla de acá y no un comentario sobre cómo vas — recargá la página.',
  'prog.headline': 'del plan, ponderado por cuánto vale cada materia',
  'prog.headline.none':
    'Todavía no hay nada que medir: no se han escrito preguntas para las materias que estás estudiando.',
  'prog.counts':
    '{holding} sostenidas · {struggling} por trabajar · {untested} sin preguntar todavía, de {questions} preguntas.',
  'prog.answered.none':
    'Todavía no te han preguntado nada, así que no se sabe nada de cómo vas. Es un resultado vacío, no uno malo.',
  'prog.answered':
    'Respondiste {answered} de {questions} preguntas. Sobre esas {answered}, y solo esas, nabu calcula en {recall} tu posibilidad de acertar hoy.',
  'prog.pace.score':
    'No hay fecha con la cual estar al día. Pediste sostener {target}, que es el estándar contra el que nabu programa.',
  'prog.pace.date.ok':
    'Quedan {days} días. El plan necesita unas {required} preguntas por día y vos aceptás hasta {capacity}, así que todavía nada lo vuelve imposible — que no es lo mismo que una promesa.',
  'prog.pace.date.tight':
    'Quedan {days} días. El plan necesita unas {required} preguntas por día y vos aceptás {capacity}. No cabe: movés la fecha, destildás materias, o aceptás más preguntas por día.',
  'prog.load.title': 'Hoy',
  'prog.load':
    'Hasta {limit} preguntas hoy. {sent} hechas, {answered} respondidas, y {due} listas para preguntarte.',
  'prog.load.noLimit':
    'Todavía no guardaste un horario, así que no hay un tope diario tuyo contra el cual reportar.',
  'prog.load.ceiling':
    'nabu pregunta de una en una y espera tu respuesta, así que {limit} por día es un techo y no un pronóstico. La mayoría de los días van a ser menos. Las lecciones cortas que te enseñan un tema, y cualquier recordatorio de una pregunta que sigue esperando, no cuentan.',
  'prog.load.waiting': 'Hay una pregunta pendiente. No sale nada nuevo hasta que la respondás.',
  'prog.load.paused': 'El envío está detenido, así que no está saliendo nada.',
  'prog.load.pausedBySilence':
    'El envío se detuvo solo después de tres recordatorios sin respuesta. Volvé a activarlo abajo y todo sigue donde quedó.',
  'prog.load.noZone':
    'No sabemos en qué zona horaria estás, así que nabu no puede decir qué día es donde vos estás — la cuenta de arriba puede ir contra el día equivocado.',
  'prog.subjects.title': 'Materia por materia',
  'prog.subjects.byPlan': 'Verlas en el orden del plan',
  'prog.subjects.byWeakest': 'Ver primero las más flojas',
  'prog.subject.counts':
    '{holding} de {questions} sostenidas · {struggling} por trabajar · {untested} sin preguntar',
  'prog.subject.none': 'Todavía sin preguntas',
  'prog.note.explanations':
    '{count} de los mensajes de este plan son explicaciones y no preguntas. Nadie responde esas, así que quedan fuera de todos los números de arriba.',
  'prog.note.noQuestions':
    '{count} materias que estás estudiando no tienen preguntas escritas. Quedan fuera de la cifra de arriba en vez de contar como cero.',
  'prog.note.unplaced':
    '{count} preguntas son de temas que ya no están en el plan. Se siguen preguntando, y no se cuentan en ninguna materia.',
  'prog.note.off':
    'Una materia que destildaste después de empezar todavía tiene sus preguntas en la rotación — destildar evita que se escriban nuevas, no que salgan las que ya estaban programadas.',
  'prog.note.partial':
    'Acá hay más preguntas de las que esta pantalla lee de una vez, así que cada número de arriba es un piso y no un total.',

  'sheet.title': 'Lo que has aprendido',
  'sheet.hint':
    'Una línea por cada cosa que ya te preguntaron y respondiste — no todo el plan, y nada que todavía no te hayan enseñado. Hecho para imprimir.',
  'sheet.download': 'Descargarlo en PDF',
  'sheet.working': 'Escribiéndolo…',
  'sheet.error.empty':
    'Todavía no respondiste nada, así que no hay nada que poner en la hoja. Se va llenando a medida que respondés preguntas por WhatsApp.',
  'sheet.error.rewrite':
    'Esta vez no se pudo escribir la hoja. No se perdió nada — probá de nuevo en un momento y sigue donde quedó.',
  'sheet.error.generic': 'No se pudo descargar la hoja. Probá de nuevo.',

  'win.title': 'Cuándo te puede escribir nabu',
  'win.hint':
    'Esto queda fijo, no es parte de ningún objetivo — se mantiene como lo dejés cuando cerrás uno y arrancás otro.',
  'win.loading': 'Cargando…',
  'win.defaults': 'Todavía no guardaste nada, así que esto es lo que viene por defecto.',
  'win.limit.label': 'Cuántas preguntas por día, como máximo',
  'win.limit.unit': 'preguntas por día',
  'win.hours.label': 'Las horas en que estás disponible',
  'win.hours.from': 'Desde',
  'win.hours.until': 'Hasta',
  'win.hours.hint':
    'Fuera de esas horas no se manda nada — espera. Las dos horas se leen en tu propio reloj.',
  'win.explanations.label': 'Mandar explicaciones, no solo preguntas',
  'win.explanations.hint':
    'Apagado, nabu solo te pregunta cosas. Encendido, te enseña el tema en unas pocas cientos de palabras antes de preguntarte, y te lo vuelve a enseñar si empezás a fallar. Eso no te gasta preguntas del día. En los dos casos, si fallás algo se te corrige igual.',
  'win.plain': '{count} preguntas por día entre las {from} y las {until} — más o menos una cada {gap}.',
  'win.plain.one': 'Una pregunta por día, en algún momento entre las {from} y las {until}.',
  'win.plain.quiet': 'Nada del todo entre las {until} y las {from}.',
  'win.plain.explanations.on':
    'Las lecciones cortas llegan aparte, las que haga falta — son las que te enseñan un tema antes de preguntarte, así que no te gastan preguntas.',
  'win.plain.explanations.off': 'Solo preguntas. No se manda nada para leer antes.',
  'win.unit.hours': 'h',
  'win.unit.minutes': 'min',
  'win.zone': 'Se leen en el reloj de donde estás:',
  'win.zone.missing':
    'No sabemos en qué zona horaria estás, así que estas horas todavía no se pueden respetar — horas sin zona horaria no son un horario. Volvé a entrar desde el teléfono que usás y se toma sola.',
  'win.save': 'Guardar esto',
  'win.saved': 'Guardado.',
  'win.working': 'Guardando…',
  'win.error.load': 'No se pudo leer tu horario actual. Recargá la página.',
  'win.error.limit': 'Entre 1 y 24 preguntas por día. Cero no es un horario — mejor cerrá el objetivo.',
  'win.error.limitWhole': 'Un número entero de preguntas por día.',
  'win.error.time': 'Esas horas no se pudieron leer. Usá los dos selectores de hora.',
  'win.error.empty': 'La hora de inicio y la de fin son la misma, así que no queda ninguna hora para escribirte.',
  'win.error.wrap':
    'Las horas tienen que empezar y terminar el mismo día. Si querés que te escriba de noche, terminá a las 23:59 y no pasada la medianoche.',
  'win.error.generic': 'No se guardó. Probá de nuevo.',

  'chat.title': 'La conversación',
  'chat.lede':
    'El mismo hilo que te llega por WhatsApp: las preguntas, lo que respondiste y lo que te dijimos. Si respondés acá, se califica igual.',
  'chat.loading': 'Leyendo la conversación…',
  'chat.empty':
    'Todavía no hay nada. La primera pregunta llega cuando haya un objetivo corriendo y estés dentro de las horas que elegiste.',
  'chat.older': 'Ver mensajes anteriores',
  'chat.input.label': 'Tu respuesta',
  'chat.input.placeholder': 'Respondé la pregunta, o pedí algo.',
  'chat.send': 'Enviar',
  'chat.sending': 'Enviando…',
  'chat.retry': 'Enviar de nuevo',
  'chat.said.you': 'Dijiste:',
  'chat.said.nabu': 'nabu dijo:',
  'chat.hint':
    'También podés responder MÁS para más detalle de lo último que se te enseñó, RECLAMO si una calificación está mal, o «5 MÁS PREGUNTAS» para seguir cuando ya se acabó el día.',
  'chat.polling':
    'Los mensajes nuevos aparecen en menos de veinte segundos mientras esta pestaña esté abierta.',
  'chat.error.load': 'No se pudo leer la conversación. Recargá la página.',
  'chat.error.empty': 'No hay nada que enviar.',
  'chat.error.long':
    'Es más largo de lo que la conversación puede guardar, así que quedaría guardado distinto de como se leyó. Acortalo y envialo de nuevo.',
  'chat.error.session': 'Esta sesión ya no es válida. Entrá de nuevo.',
  'chat.error.graded':
    'Tu respuesta sí se calificó, pero la contestación no llegó de vuelta. Enviarla otra vez no ayuda: la pregunta ya se cerró y la siguiente llega igual.',
  'chat.error.send': 'No se envió. Probá de nuevo.',
  'chat.error.offline': 'Sin conexión. Probá cuando tengas.',
  'push.title': 'Notificaciones en este dispositivo',
  'push.hint':
    'nabu puede escribirte por aquí en lugar de WhatsApp. Actívalas una vez en cada teléfono, tableta o computadora donde quieras que te pregunte: es una preferencia de este dispositivo, no de tu cuenta.',
  'push.checking': 'Revisando…',
  'push.on': 'Activadas en este dispositivo.',
  'push.off': 'Desactivadas en este dispositivo.',
  'push.enable': 'Activar notificaciones',
  'push.disable': 'Desactivarlas aquí',
  'push.working': 'Un momento…',
  'push.devices.none': 'Todavía no hay ningún dispositivo registrado.',
  'push.devices.one': 'Un dispositivo registrado.',
  'push.devices.many': '{count} dispositivos registrados.',
  'push.stale':
    'Este dispositivo cree que las notificaciones están activas, pero nabu no tiene registro de él. Desactívalas y vuelve a activarlas aquí.',
  'push.install.title': 'Primero agrega nabu a tu pantalla de inicio',
  'push.install.body':
    'En iPhone y iPad las notificaciones solo funcionan cuando la app está en la pantalla de inicio. Tocá Compartir, luego “Agregar a inicio”, abrí nabu desde ahí y volvé a esta pantalla.',
  'push.unsupported':
    'Este navegador no puede mostrar notificaciones. Chrome, Safari 16.4 o posterior y Firefox sí pueden.',
  'push.denied':
    'Este navegador está bloqueando las notificaciones de nabu. Desde aquí no se puede deshacer: hay que cambiarlo en la configuración del navegador para este sitio y después podés activarlas de nuevo.',
  'push.error.notConfigured':
    'Las notificaciones todavía no están configuradas en este servicio. No es un problema de tu dispositivo.',
  'push.error.generic': 'No se pudo. Probá de nuevo.',
  'push.test': 'Enviarme una prueba',
  'push.test.hint':
    'Llega por donde nabu te escribe hoy: si seguís en WhatsApp, llega ahí. De eso se trata, te dice cuál está activo.',
  'push.test.sent': 'Va en camino. Si no llega nada en un minuto, las notificaciones no te están llegando.',
  'push.test.working': 'Enviando…',
  'push.test.error': 'No se pudo enviar la prueba. Probá de nuevo en un momento.',

  'footer.built': 'Hecho en Costa Rica.',
};

const DICTIONARIES: Record<Locale, Record<CopyKey, string>> = { en, es };

export const LOCALE_STORAGE_KEY = 'nabu.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * The locale to start in.
 *
 * A remembered choice always wins — somebody who switched to English meant it.
 * Otherwise the browser's preference, and Spanish if it says nothing useful.
 * Wrapped because a browser with site data blocked throws on the read rather
 * than returning null, and a landing page must not fail to render over that.
 */
export function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage unavailable. Fall through to the browser's preference.
  }
  const preferred = typeof navigator === 'undefined' ? '' : navigator.language.slice(0, 2);
  return isLocale(preferred) ? preferred : DEFAULT_LOCALE;
}

export interface I18n {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: CopyKey) => string;
}

export const I18nContext = createContext<I18n | null>(null);

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n used outside the provider');
  return value;
}

/**
 * A localised map resolved for the reader, then for anybody.
 *
 * Falls back to whatever language the text was generated in before falling back
 * to the placeholder: a topic titled in Spanish on an English screen is still
 * the name of that topic, and far more use than "Topic without a title".
 *
 * It lives here rather than beside either of its callers because objective
 * titles and curriculum nodes were on course to grow two fallbacks that
 * disagreed — one screen showing the Spanish title and the next showing the
 * placeholder for the same objective.
 */
export function localised(copy: Localised, locale: Locale, fallback: string): string {
  return copy[locale] ?? Object.values(copy)[0] ?? fallback;
}

export function translate(locale: Locale, key: CopyKey): string {
  return DICTIONARIES[locale][key];
}
